
import { defineComponent } from 'vue'
import { dollarsToCents } from '~/utils/billing'
import { debounce } from '~/utils/debounce'
import type { V2PlanConfig } from './v2PlanConfig'
import { TEAM_PLAN } from './v2PlanConfig'

/** Convert cents to a dollar string for form inputs. Returns '' for zero/falsy. */
function centsToDollarsDisplay(cents: number): string {
  if (!cents) return ''
  return (cents / 100).toString()
}

export default defineComponent({
  name: 'V2ActivePlanCard',
  props: {
    name: {
      type: String,
      required: true
    },
    price: {
      type: String,
      required: true
    },
    contributors: {
      type: Number,
      default: 0
    },
    ossTargets: {
      type: Number,
      default: 0
    },
    aiCreditBalance: {
      type: Number,
      default: 0
    },
    autoRechargeEnabled: {
      type: Boolean,
      default: false
    },
    balanceThreshold: {
      type: Number,
      default: 0
    },
    autoRechargeAmount: {
      type: Number,
      default: 0
    },
    maxRechargePerMonth: {
      type: Number,
      default: 0
    },
    nextRenewal: {
      type: String,
      default: ''
    },
    billingInterval: {
      type: String,
      default: 'Monthly'
    },
    promotionalCredits: {
      type: Number,
      default: 0
    },
    currentInvoiceAmount: {
      type: Number,
      default: 0
    },
    isTrial: {
      type: Boolean,
      default: false
    },
    cancelAtPeriodEnd: {
      type: Boolean,
      default: false
    },
    resuming: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showIncluded: false,
      autoRechargeLocal: this.autoRechargeEnabled,
      thresholdLocal: centsToDollarsDisplay(this.balanceThreshold),
      rechargeAmountLocal: centsToDollarsDisplay(this.autoRechargeAmount),
      maxRechargeLocal: centsToDollarsDisplay(this.maxRechargePerMonth),
      debouncedEmitSettings: null as ReturnType<typeof debounce> | null
    }
  },
  computed: {
    badgeLabel(): string {
      if (this.isTrial) return 'TRIAL'
      if (this.cancelAtPeriodEnd) return 'CANCELLING'
      return 'ACTIVE'
    },
    badgeColor(): string {
      if (this.isTrial) return 'honey'
      if (this.cancelAtPeriodEnd) return 'cherry'
      return 'aqua'
    },
    teamPlanConfig(): V2PlanConfig {
      return TEAM_PLAN
    },
    formattedCreditBalance(): string {
      return `$${this.aiCreditBalance.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    },
    formattedPromotionalCredits(): string {
      return `$${this.promotionalCredits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    },
    formattedInvoiceAmount(): string {
      return `$${this.currentInvoiceAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    },
    usageRoute(): string {
      return this.$generateRoute(['settings', 'usage'], false, true)
    }
  },
  watch: {
    autoRechargeEnabled(val: boolean) {
      this.autoRechargeLocal = val
    },
    balanceThreshold(val: number) {
      this.thresholdLocal = centsToDollarsDisplay(val)
    },
    autoRechargeAmount(val: number) {
      this.rechargeAmountLocal = centsToDollarsDisplay(val)
    },
    maxRechargePerMonth(val: number) {
      this.maxRechargeLocal = centsToDollarsDisplay(val)
    },
    autoRechargeLocal(val: boolean) {
      if (val !== this.autoRechargeEnabled) {
        this.$emit('update-auto-topup', { isAutoTopupEnabled: val })
      }
    }
  },
  created() {
    this.debouncedEmitSettings = debounce(() => {
      this.$emit('update-auto-topup', {
        isAutoTopupEnabled: this.autoRechargeLocal,
        autoTopupThreshold: dollarsToCents(this.thresholdLocal),
        autoTopupAmount: dollarsToCents(this.rechargeAmountLocal),
        autoTopupMax: dollarsToCents(this.maxRechargeLocal) || null
      })
    }, 1000)
  },
  methods: {
    onCancelClick(close: () => void): void {
      close()
      this.$emit('cancel-subscription')
    },
    onSettingsInput(): void {
      this.debouncedEmitSettings?.()
    }
  }
})
